// composables/useSipUserAgent.js
import { ref, shallowRef } from 'vue';
import { UserAgent, Registerer, Inviter, SessionState } from 'sip.js';
import { toast } from 'vue-sonner';

// Global state (optional: move inside function if you want separate instances per component)
const isConnected = ref(false);
const isRegistered = ref(false);
const incomingSession = shallowRef(null); // The current call session
const incomingCallIntent = ref(null);
const currentCall = ref(null)

const currentSession = shallowRef(null);
const agentStatus  = ref(null)
const loadingFetchingCustomerIntent = ref(false)
const loadingSetupUserAgent = ref(false)
const loadingMakeCall = ref(false)
const loadingAnswerCall = ref(false)
const loadingRejectCall = ref(false)
const loadingHangupCall = ref(false)
const loadingDisconnect = ref(false)
const loadingGettingAgentStatus = ref(false)
const loadingSettingAgentStatus = ref(false)
const loadingGettingCurrentCall = ref(false)



export function useSipUserAgent() {
  let userAgent = null;
  let registerer = null;

  // Configuration for the SIP User Agent
  const setupUserAgent = async (config) => {
    loadingSetupUserAgent.value = true;
    try {
      const {
        extension,
        password,
        domain =  import.meta.env.VITE_FREESWITCH_DOMAIN,
        wssPort = '7443' // FreeSWITCH default WSS port
      } = config;

      // 1. Define the transport URI (Secure WebSocket)
      // Note: FreeSWITCH often uses port 7443 for WSS or 5066.
      // If you are using Nginx as a proxy, it might be just "wss://pbx.umisis.com/ws"
      const serverUrl = `wss://${domain}:${wssPort}`;

      const uri = UserAgent.makeURI(`sip:${extension}@${domain}`);
      if (!uri) throw new Error("Failed to create URI");

      // 2. Configure SIP.js
      const userAgentOptions = {
        uri: uri,
        transportOptions: {
          server: serverUrl,
        },
        authorizationUsername: extension,
        authorizationPassword: password,
        sessionDescriptionHandlerFactoryOptions: {
          peerConnectionConfiguration: {
            iceServers: [
              {
                urls: ["turns:turn.umisis.com:443?transport=tcp"],
                username: "umisis",
                credential: "Um1sisC0T4rn"
              }
            ]
          }
        },
        delegate: {
          // Handle Incoming Invites (Calls)
          onConnect: () => {
            isConnected.value = true;
            console.log('SIP: WebSocket Connected');
          },
          onDisconnect: (error) => {
            isConnected.value = false;
            isRegistered.value = false;
            console.log('SIP: WebSocket Disconnected', error);

            // Optional: Reconnect logic could go here
          },
          onInvite: (invitation) => {
            console.log('SIP: Incoming call received');
            console.log("📞 Incoming Call Detected!");
            handleIncomingCall(invitation);
          },
        },
      };

      userAgent = new UserAgent(userAgentOptions);

      // 3. Start the User Agent
      await userAgent.start();

      // 4. Register the extension
      registerer = new Registerer(userAgent);

      // Track registration state changes
      registerer.stateChange.addListener((newState) => {
        switch (newState) {
        case 'Registered':
          isRegistered.value = true;
          break;
        case 'Unregistered':
        case 'Terminated':
          isRegistered.value = false;
          break;
        }
      });

      await registerer.register();
    } catch (e) {
      console.error(e);
      toast.error('Failed to connect to PBX.');
      throw e;
    } finally {
      loadingSetupUserAgent.value = false;
    }
  };

  // --- Session Management ---

  const fetchCustomerIntentByUUID = async (uuid) => {
    try {
      console.log('fetching call intent for ' + uuid)
      loadingFetchingCustomerIntent.value = true
      const { data } = await axios.get(`/api/v1/pbx/incoming-call/${uuid}`);
      incomingCallIntent.value = data;
      loadingFetchingCustomerIntent.value = false
    }
    catch(e) {
      loadingFetchingCustomerIntent.value = false
      console.error(e);
    }
    finally {
      loadingFetchingCustomerIntent.value = false
    }
  }
  const handleIncomingCall = (invitation) => {
    incomingSession.value = invitation;
    console.log(invitation)
    // we need the uuid to do a call to the server to fetch the customer intent data
    // fetchCustomerIntentByUUID('ref_id')

    invitation.stateChange.addListener((newState) => {
      if (newState === SessionState.Terminated) {
        incomingSession.value = null;
        currentSession.value = null;
      } else if (newState === SessionState.Established) {
        currentSession.value = invitation;
        incomingSession.value = null;
        setupRemoteMedia(invitation);
      }
    });
  };

  const answerCall = async () => {
    if (!incomingSession.value) return;
    loadingAnswerCall.value = true;
    try {
      await incomingSession.value.accept();
      await getCurrentCall();
    } catch (e) {
      console.error(e);
      toast.error('Failed to answer the call.');
    } finally {
      loadingAnswerCall.value = false;
    }
  };

  const rejectCall = async () => {
    if (!incomingSession.value) return;
    loadingRejectCall.value = true;
    try {
      await incomingSession.value.reject();
      incomingCallIntent.value = null
    } catch (e) {
      console.error(e);
      toast.error('Failed to reject the call.');
    } finally {
      loadingRejectCall.value = false;
    }
  };

  const hangupCall = async () => {
    if (!currentSession.value) return;
    loadingHangupCall.value = true;
    try {
      incomingCallIntent.value = null
      // Depending on state, we either cancel (if ringing) or bye (if active)
      switch (currentSession.value.state) {
      case SessionState.Established:
        await currentSession.value.bye();
        break;
      case SessionState.Establishing:
        // If we are the caller and cancelling
        if(currentSession.value instanceof Inviter) {
          await currentSession.value.cancel();
        }
        break;
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to hang up the call.');
    } finally {
      loadingHangupCall.value = false;
    }
  };

  const makeCall = async (targetExtension) => {
    loadingMakeCall.value = true;
    try {
      if (!userAgent) throw new Error("User Agent not initialized");

      const targetURI = UserAgent.makeURI(`sip:${targetExtension}@${userAgent.configuration.uri.host}`);
      if (!targetURI) throw new Error("Invalid Target URI");

      const inviter = new Inviter(userAgent, targetURI);
      currentSession.value = inviter;

      // Handle state changes (ringing, connected, hung up)
      inviter.stateChange.addListener((newState) => {
        if (newState === SessionState.Established) {
          setupRemoteMedia(inviter);

        } else if (newState === SessionState.Terminated) {
          currentSession.value = null;
        }
      });

      // Send the invite
      await inviter.invite();
      await getCurrentCall();
    } catch (e) {
      console.error(e);
      toast.error('Failed to place the call.');
    } finally {
      loadingMakeCall.value = false;
    }
  };

  // Helper to attach audio stream to the DOM
  const setupRemoteMedia = (session) => {
    const remoteStream = new MediaStream();
    session.sessionDescriptionHandler.peerConnection.getReceivers().forEach((receiver) => {
      if (receiver.track) {
        remoteStream.addTrack(receiver.track);
      }
    });

    // We need a specific HTMLAudioElement to play the sound
    const audioEl = document.getElementById('remoteAudio');
    if (audioEl) {
      audioEl.srcObject = remoteStream;
      audioEl.play();
    }
  };

  const disconnect = async () => {
    loadingDisconnect.value = true;
    try {
      if (registerer) await registerer.unregister();
      if (userAgent) await userAgent.stop();
      isConnected.value = false;
      isRegistered.value = false;
    } catch (e) {
      console.error(e);
      toast.error('Failed to disconnect from PBX.');
    } finally {
      loadingDisconnect.value = false;
    }
  };


  const getCurrentCall = async () => {
    loadingGettingCurrentCall.value = true;
    try {
      const { data } = await axios.get('/api/v1/pbx/current-call');
      currentCall.value = data.call;
      return data.call;
    } catch (e) {
      console.error(e);
      toast.error('Failed to fetch current call.');
    } finally {
      loadingGettingCurrentCall.value = false;
    }
  }
  const getAgentStatus = async (extension) => {
    loadingGettingAgentStatus.value = true;
    try {
      const { data } = await axios.get('/api/v1/pbx/agent-status', {
        params: {extension}
      });
      agentStatus.value = data.status;
      return data.status;
    } catch (e) {
      console.error(e);
      toast.error('Failed to fetch agent status' + e.message);
    } finally {
      loadingGettingAgentStatus.value = false;
    }
  }
  const setAgentStatus = async (extension, status) => {
    loadingSettingAgentStatus.value = true;
    try {
      await axios.post('/api/v1/pbx/agent-status',  {
        extension, status
      });
      return getAgentStatus(extension);
    } catch (e) {
      console.error(e);
      toast.error('Failed to update agent status:' + e.response.data.message);
    } finally {
      loadingSettingAgentStatus.value = false;
    }
  }

  return {
    isConnected,
    isRegistered,
    incomingSession,
    incomingCallIntent,
    currentSession,
    setupUserAgent,
    makeCall,
    answerCall,
    rejectCall,
    hangupCall,
    disconnect,
    getAgentStatus,
    setAgentStatus,
    loadingSetupUserAgent,
    loadingMakeCall,
    loadingAnswerCall,
    loadingRejectCall,
    loadingHangupCall,
    loadingDisconnect,
    loadingGettingAgentStatus,
    loadingSettingAgentStatus,
    agentStatus,
    currentCall,
    getCurrentCall,
    loadingGettingCurrentCall
  };
}
